import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronDown, Copy, Link2, Loader2, Mail, Search, X } from 'lucide-react';
import toast from 'react-hot-toast';
import type { Academy, Lesson, StudentProfile, Term } from '@court-sync/shared';
import { isStarterRequestBookingAcademy, resolvePublicLessonShareUrl } from '@court-sync/shared';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAcademy } from '@/queries/academy';
import type { LessonEnrollmentRow } from '@/services/lesson';
import { lessonService } from '@/services/lesson';
import { academyStudentService } from '@/services/academyStudentService';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function firstValidEmail(...emails: (string | null | undefined)[]): string | null {
  for (const raw of emails) {
    const email = raw?.trim().toLowerCase();
    if (email && EMAIL_RE.test(email)) return email;
  }
  return null;
}

function studentRecipientEmail(
  student: StudentProfile,
  enrollmentByStudentId?: Map<string, LessonEnrollmentRow>
): string | null {
  const fromProfile = firstValidEmail(student.guardianEmail, student.inviteEmail);
  if (fromProfile) return fromProfile;

  const enrollment = enrollmentByStudentId?.get(student.id);
  if (!enrollment) return null;

  if (student.isSelf || enrollment.isSelf) {
    return firstValidEmail(enrollment.studentEmail, enrollment.parentEmail);
  }
  return firstValidEmail(enrollment.parentEmail, enrollment.studentEmail);
}

function publicBookingLinkDescription(
  lessonType: Lesson['lessonType'] | undefined,
  starterRequestBooking: boolean
): string {
  if (starterRequestBooking) {
    return 'Families can use this link to view this lesson and submit a booking request.';
  }
  switch (lessonType) {
    case 'camp':
      return 'Families can use this link to view and book this camp online.';
    case 'group':
      return 'Families can use this link to view and book this group lesson online.';
    case 'private':
      return 'Guardians can use this link to book and pay for this private lesson online.';
    default:
      return 'Families can use this link to view and book this lesson online.';
  }
}

type RecipientRow = {
  id: string;
  name: string;
  email: string;
  isEnrolled: boolean;
  isManual?: boolean;
};

export interface LessonShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  academyId: string;
  /** When omitted, loaded from academyId via useAcademy. */
  academy?: Pick<Academy, 'slug' | 'subdomain' | 'subdomainEnabled' | 'subscriptionTier'>;
  lessonId: string;
  lessonName: string;
  lessonType?: Lesson['lessonType'];
  lessonTermId?: string | null;
  lessonIsActive?: boolean;
  term?: Pick<Term, 'id' | 'endDate'> | null;
  enrollments: LessonEnrollmentRow[];
  onAfterNotify?: () => void | Promise<void>;
  stackLevel?: 'default' | 'elevated';
}

export function LessonShareModal({
  isOpen,
  onClose,
  academyId,
  academy: academyProp,
  lessonId,
  lessonName,
  lessonType,
  lessonTermId,
  lessonIsActive = true,
  term = null,
  enrollments,
  onAfterNotify,
  stackLevel = 'elevated',
}: LessonShareModalProps) {
  const { academy: fetchedAcademy } = useAcademy(academyId, { enabled: !academyProp });
  const academy = academyProp ?? fetchedAcademy;

  const starterRequestBooking = academy ? isStarterRequestBookingAcademy(academy) : false;

  const bookingLink = useMemo(() => {
    if (!academy?.slug) return '';
    const lesson: Pick<Lesson, 'id' | 'lessonType' | 'termId' | 'isActive'> = {
      id: lessonId,
      lessonType: lessonType ?? 'group',
      termId: lessonTermId ?? undefined,
      isActive: lessonIsActive,
    };
    return resolvePublicLessonShareUrl(academy, lesson, term);
  }, [academy, lessonId, lessonType, lessonTermId, lessonIsActive, term]);

  const enrolledStudentIds = useMemo(
    () =>
      new Set(
        enrollments
          .filter((r) => r.bookingId && r.enrollmentSource !== 'makeup_session')
          .map((r) => r.studentId)
      ),
    [enrollments]
  );

  const [emailExpanded, setEmailExpanded] = useState(false);
  const [recipients, setRecipients] = useState<RecipientRow[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [searchResults, setSearchResults] = useState<StudentProfile[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [resultsOpen, setResultsOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [copied, setCopied] = useState(false);
  const searchWrapRef = useRef<HTMLDivElement>(null);

  const recipientIds = useMemo(() => new Set(recipients.map((r) => r.id)), [recipients]);

  const enrollmentByStudentId = useMemo(
    () => new Map(enrollments.map((row) => [row.studentId, row])),
    [enrollments]
  );

  useEffect(() => {
    const t = window.setTimeout(() => setSearchDebounced(searchQuery.trim()), 300);
    return () => window.clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    if (!isOpen) {
      setEmailExpanded(false);
      setRecipients([]);
      setSearchQuery('');
      setSearchDebounced('');
      setSearchResults([]);
      setResultsOpen(false);
      setCopied(false);
      return undefined;
    }
    return undefined;
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !emailExpanded || !academyId || searchDebounced.length < 1) {
      setSearchResults([]);
      setSearchLoading(false);
      return undefined;
    }

    let cancelled = false;
    setSearchLoading(true);
    void academyStudentService
      .getAcademyStudentsPaginated(academyId, 1, 25, { searchQuery: searchDebounced })
      .then((res) => {
        if (!cancelled) setSearchResults(res.data ?? []);
      })
      .catch(() => {
        if (!cancelled) setSearchResults([]);
      })
      .finally(() => {
        if (!cancelled) setSearchLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [isOpen, emailExpanded, academyId, searchDebounced]);

  useEffect(() => {
    if (!resultsOpen) return undefined;
    const onDoc = (e: MouseEvent) => {
      const el = searchWrapRef.current;
      if (el && !el.contains(e.target as Node)) setResultsOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [resultsOpen]);

  const searchLoadingVisible = searchDebounced.length >= 1 && searchLoading;

  const addRecipient = useCallback(
    (student: StudentProfile) => {
      const email = studentRecipientEmail(student, enrollmentByStudentId);
      if (!email) {
        toast.error('This student has no guardian or invite email on file');
        return;
      }
      if (recipientIds.has(student.id)) {
        toast.error('Student already added');
        return;
      }
      setRecipients((prev) => [
        ...prev,
        {
          id: student.id,
          name: `${student.firstName} ${student.lastName}`.trim(),
          email,
          isEnrolled: enrolledStudentIds.has(student.id),
          isManual: student.isManual,
        },
      ]);
      setSearchQuery('');
      setSearchDebounced('');
      setSearchResults([]);
      setResultsOpen(false);
    },
    [enrolledStudentIds, enrollmentByStudentId, recipientIds]
  );

  const removeRecipient = (studentId: string) => {
    setRecipients((prev) => prev.filter((r) => r.id !== studentId));
  };

  const copyBookingLink = async () => {
    try {
      await navigator.clipboard.writeText(bookingLink);
      setCopied(true);
      toast.success('Booking link copied');
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Failed to copy link');
    }
  };

  const toggleEmailPanel = () => {
    setEmailExpanded((open) => !open);
  };

  const handleSendEmails = useCallback(async () => {
    const studentIds = recipients.map((r) => r.id);
    if (studentIds.length === 0) {
      toast.error('Add at least one student recipient');
      return;
    }

    setSending(true);
    try {
      const res = await lessonService.shareLessonLink(lessonId, { academyId, studentIds });
      if (res.success && res.data) {
        const { sent, skipped, skippedNoEmail } = res.data;
        if (sent > 0) {
          toast.success(`Sent booking link to ${sent} recipient${sent === 1 ? '' : 's'}`);
        }
        if (skipped > 0) {
          const names = skippedNoEmail.slice(0, 3).join(', ');
          const suffix =
            skippedNoEmail.length > 3 ? ` and ${skippedNoEmail.length - 3} more` : '';
          toast.error(
            `${skipped} student${skipped === 1 ? '' : 's'} skipped (no email on file${names ? `: ${names}${suffix}` : ''})`
          );
        }
        await onAfterNotify?.();
        if (sent > 0) onClose();
      } else {
        toast.error((res as { error?: string }).error || 'Failed to send emails');
      }
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to send emails');
    } finally {
      setSending(false);
    }
  }, [academyId, lessonId, onAfterNotify, onClose, recipients]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Share — ${lessonName}`}
      maxWidth="lg"
      stackLevel={stackLevel}
    >
      <div className="space-y-5 text-sm text-gray-800 dark:text-gray-200">
        <section className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200">
              <Link2 className="h-4 w-4" aria-hidden />
            </div>
            <div className="min-w-0 flex-1 space-y-1">
              <p className="font-medium text-gray-900 dark:text-white">Public booking link</p>
              <p className="text-xs text-muted-foreground">
                {publicBookingLinkDescription(lessonType, starterRequestBooking)}
              </p>
            </div>
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50/80 p-3 dark:border-gray-700 dark:bg-gray-900/40">
            <p className="break-all font-mono text-xs text-gray-700 dark:text-gray-300">{bookingLink}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={() => void copyBookingLink()}
            >
              {copied ? (
                <Check className="h-4 w-4" aria-hidden />
              ) : (
                <Copy className="h-4 w-4" aria-hidden />
              )}
              {copied ? 'Copied' : 'Copy link'}
            </Button>
            <Button
              type="button"
              variant={emailExpanded ? 'default' : 'outline'}
              size="sm"
              className="gap-1.5"
              onClick={toggleEmailPanel}
              aria-expanded={emailExpanded}
            >
              <Mail className="h-4 w-4" aria-hidden />
              Email link
              <ChevronDown
                className={`h-4 w-4 transition-transform ${emailExpanded ? 'rotate-180' : ''}`}
                aria-hidden
              />
            </Button>
          </div>
        </section>

        {emailExpanded && (
          <section className="space-y-4 border-t border-gray-200 pt-5 dark:border-gray-700">
            <div>
              <p className="font-medium text-gray-900 dark:text-white">Email booking link</p>
              <p className="mt-0.5 text-xs text-muted-foreground">
                Search your roster and add recipients. Students without an email cannot be selected.
              </p>
            </div>

            <div ref={searchWrapRef} className="relative">
              <div className="relative">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  aria-hidden
                />
                <Input
                  type="search"
                  autoComplete="off"
                  value={searchQuery}
                  onChange={(e) => {
                    setSearchQuery(e.target.value);
                    setResultsOpen(true);
                  }}
                  onFocus={() => setResultsOpen(true)}
                  placeholder="Search students by name…"
                  className="pl-9 pr-10"
                  aria-label="Search students to add as recipients"
                  aria-autocomplete="list"
                  aria-expanded={resultsOpen}
                  aria-controls="lesson-share-student-results"
                />
                {searchLoadingVisible && (
                  <Loader2
                    className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
                    aria-hidden
                  />
                )}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                Includes linked accounts, manual students, and roster-only entries.
              </p>

              {resultsOpen && searchDebounced.length >= 1 && (
                <ul
                  id="lesson-share-student-results"
                  role="listbox"
                  className="absolute z-20 mt-1 max-h-48 w-full overflow-y-auto rounded-md border border-border bg-card shadow-md [scrollbar-gutter:stable]"
                >
                  {searchResults.length === 0 && !searchLoadingVisible ? (
                    <li className="px-3 py-2 text-sm text-muted-foreground">No students match.</li>
                  ) : (
                    searchResults.map((student) => {
                      const email = studentRecipientEmail(student, enrollmentByStudentId);
                      const alreadyAdded = recipientIds.has(student.id);
                      const disabled = !email || alreadyAdded;
                      return (
                        <li key={student.id} role="option" aria-selected={alreadyAdded}>
                          <button
                            type="button"
                            disabled={disabled}
                            className={`flex w-full flex-col px-3 py-2 text-left text-sm ${
                              disabled
                                ? 'cursor-not-allowed opacity-60'
                                : 'hover:bg-muted'
                            }`}
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => addRecipient(student)}
                          >
                            <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                              <span className="font-medium text-foreground">
                                {student.firstName} {student.lastName}
                              </span>
                              {student.isManual && (
                                <span className="rounded bg-slate-100 px-1.5 py-0 text-[10px] font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                                  Manual
                                </span>
                              )}
                              {enrolledStudentIds.has(student.id) && (
                                <span className="rounded bg-emerald-100 px-1.5 py-0 text-[10px] font-medium text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-100">
                                  Enrolled
                                </span>
                              )}
                              {alreadyAdded && (
                                <span className="text-[10px] text-muted-foreground">Added</span>
                              )}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {email ?? 'No email on file'}
                            </span>
                          </button>
                        </li>
                      );
                    })
                  )}
                </ul>
              )}
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Recipients ({recipients.length})
                </p>
                {recipients.length > 0 && (
                  <button
                    type="button"
                    className="text-xs font-medium text-gray-500 hover:underline dark:text-gray-400"
                    onClick={() => setRecipients([])}
                  >
                    Clear all
                  </button>
                )}
              </div>
              {recipients.length === 0 ? (
                <p className="rounded-lg border border-dashed border-gray-300 px-3 py-6 text-center text-xs text-muted-foreground dark:border-gray-600">
                  Search above to add students who should receive this link.
                </p>
              ) : (
                <ul className="space-y-2 rounded-lg border border-gray-200 p-2 dark:border-gray-700">
                  {recipients.map((row) => (
                    <li
                      key={row.id}
                      className="flex items-start justify-between gap-2 rounded-md bg-gray-50 px-2.5 py-2 dark:bg-gray-900/50"
                    >
                      <div className="min-w-0">
                        <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-medium text-gray-900 dark:text-white">
                          {row.name}
                          {row.isManual && (
                            <span className="rounded bg-slate-100 px-1.5 py-0 text-[10px] font-medium text-slate-700 dark:bg-slate-800 dark:text-slate-300">
                              Manual
                            </span>
                          )}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">{row.email}</p>
                      </div>
                      <button
                        type="button"
                        className="shrink-0 rounded p-1 text-gray-500 hover:bg-gray-200 hover:text-gray-800 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                        onClick={() => removeRecipient(row.id)}
                        aria-label={`Remove ${row.name}`}
                      >
                        <X className="h-4 w-4" aria-hidden />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </section>
        )}

        <div className="flex flex-col-reverse gap-2 border-t border-gray-200 pt-4 sm:flex-row sm:justify-end dark:border-gray-700">
          <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={sending}>
            {emailExpanded ? 'Cancel' : 'Done'}
          </Button>
          {emailExpanded && (
            <Button
              type="button"
              size="sm"
              className="gap-1.5"
              disabled={sending || recipients.length === 0}
              onClick={() => void handleSendEmails()}
            >
              {sending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              ) : (
                <Mail className="h-4 w-4" aria-hidden />
              )}
              {sending
                ? 'Sending…'
                : `Send link${recipients.length > 0 ? ` (${recipients.length})` : ''}`}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

/** @deprecated Use LessonShareModal */
export const PrivateLessonShareModal = LessonShareModal;
export type PrivateLessonShareModalProps = LessonShareModalProps;
